// Types for the Purchasing Module

// =============================================================================
// Enums & Literal Types
// =============================================================================

export type PurchaseOrderStatus =
  | "draft"
  | "sent"
  | "partial"
  | "received"
  | "cancelled";
export type IngredientType = "oil" | "raw_material";
// "misc" = free-text Misceláneos line (materiales y servicios no stock); no catalog reference.
export type ItemType = "oil" | "raw_material" | "product" | "misc";
export type OrderType = "ingredients" | "supplies" | "products";
export type Currency = "CLP" | "USD";
// Order-level discount applied to the neto BEFORE IVA. "percentage" = a % of the neto,
// "amount" = a fixed amount in the order currency, "none" = no discount.
export type DiscountType = "none" | "percentage" | "amount";
// P1/P2 are physical plants; NC ("No Conforme") is a quarantine bucket for
// non-conforming ingredient stock, reachable only via a traslado (never a reception).
export type Plant = "P1" | "P2" | "NC";

// =============================================================================
// Database Entities
// =============================================================================

export interface Supplier {
  id: string;
  name: string;
  rut: string | null;
  giro: string | null;
  address: string | null;
  comuna: string | null;
  phone: string | null;
  email: string | null;
  contact_name: string | null;
  notes: string | null;
  is_active: boolean;
  // Procure-to-Pay cutover fields (loaded from Softland at 31/07). RUT is not present
  // in the Softland source, so suppliers are keyed by the Softland auxiliary code.
  softland_code: string | null;
  ap_account_code: string | null; // 2-1-03-001 nacional / 2-1-03-002 extranjero
  is_foreign: boolean;
  // Master-data fields (loaded from Softland "Datos maestros proveedores")
  razon_social: string | null;
  payment_condition: string | null;
  bank_name: string | null;
  bank_account: string | null;
  // AP: remembered gasto/imputación account proposed when contabilizando this proveedor's
  // facturas (learn-as-you-go — set on confirm, editable).
  // NOTE (multiempresa AP): ap_account_code / default_expense_account_id are legacy global
  // defaults; the per-company source of truth is SupplierCompanyProfile. Kept during the
  // transition until every reader uses the profile.
  default_expense_account_id: string | null;
  created_at: string;
  updated_at: string;
}

// Per-company accounting profile for a supplier (multiempresa AP). The `suppliers` row is the
// global master (registro maestro único, one per RUT); these rows hold the per-company AP
// account, default expense account and payment terms (registro distinto por empresa contable).
export interface SupplierCompanyProfile {
  id: string;
  supplier_id: string;
  company_id: string;
  ap_account_code: string | null;
  default_expense_account_id: string | null;
  payment_condition: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined (select embeds)
  company?: { id: string; name: string } | null;
  default_expense_account?: { id: string; code: string; name: string } | null;
}

export interface SupplierCompanyProfileFormData {
  supplier_id: string;
  company_id: string;
  ap_account_code?: string | null;
  default_expense_account_id?: string | null;
  payment_condition?: string | null;
  is_active?: boolean;
}

// Aggregated open accounts-payable balance per supplier (RPC get_supplier_balances_summary).
export interface SupplierBalanceSummary {
  supplier_id: string;
  open_docs_count: number;
  saldo_total: number; // negative = amount payable to the supplier, positive = credit (NC)
  overdue_count: number;
  overdue_amount: number;
  earliest_due_date: string | null;
}

export type OpeningBalanceEstado =
  | "vencida"
  | "por_vencer"
  | "sin_fecha"
  | "credito";

// One open document in a supplier's estado de cuenta (RPC get_supplier_open_balances).
export interface SupplierOpenBalance {
  id: string;
  document_type: string | null;
  doc_tipo_code: string | null;
  folio: string | null;
  emission_date: string | null;
  due_date: string | null;
  saldo_clp: number;
  debe_clp: number;
  haber_clp: number;
  original_currency: string | null;
  original_amount: number | null;
  exchange_rate: number | null;
  glosa: string | null;
  days_to_due: number | null;
  estado: OpeningBalanceEstado;
  /** "Saldo inicial" (31/07 opening) or "Factura" (registered supplier invoice). */
  origin: string;
}

// A registered supplier invoice (factura de proveedor recibida) — going-forward AP.
export interface SupplierInvoice {
  id: string;
  supplier_id: string;
  company_id: string;
  rut: string;
  ap_account_code: string | null;
  doc_tipo_code: string;
  document_type: string | null;
  folio: string;
  emission_date: string;
  due_date: string | null;
  due_date_estimated: boolean;
  bsale_estado: string | null;
  currency: string;
  exento: number;
  neto: number;
  iva: number;
  impto_especifico: number;
  total_clp: number;
  saldo_clp: number;
  razon_social: string | null;
  source: string;
  status: string;
  // BSale DTE links (received document) so the UI can open the real factura.
  bsale_id: number | null;
  url_pdf: string | null;
  url_xml: string | null;
  // Glosa descriptiva (ítems del DTE) para la descripción del asiento.
  glosa: string | null;
  // Manual PDF fallback for docs BSale never publishes (e.g. BCI) — shown when url_pdf is null.
  manual_pdf_url: string | null;
  manual_pdf_uploaded_by: string | null;
  manual_pdf_uploaded_at: string | null;
  // AP contabilización: linked draft asiento + posting state.
  account_movement_id: string | null;
  posting_status: "sin_contabilizar" | "borrador" | "contabilizada";
  created_at: string;
  supplier?: { id: string; name: string } | null;
  company?: { id: string; name: string } | null;
}

// One row of the create_supplier_invoices_batch payload (what the BSale parser emits).
export interface SupplierInvoiceImportRow {
  rut: string;
  razon_social: string;
  ap_account_code?: string | null;
  doc_tipo_code: string;
  document_type: string;
  folio: string;
  emission_date: string;
  due_date: string | null;
  due_date_estimated: boolean;
  bsale_estado: string;
  exento: number;
  neto: number;
  iva: number;
  impto_especifico: number;
  total_clp: number;
  saldo_clp: number;
}

export interface SupplierInvoiceImportResult {
  inserted: number;
  skipped: number;
  suppliers_created: number;
  suppliers_rut_backfilled: number;
}

// Payload for manually registering a single factura (create_supplier_invoice_manual RPC).
// Amounts are already signed (AP convention: facturas negative, NC positive) — the form applies
// the sign from the chosen tipo, same as the BSale import's toImportRow.
export interface SupplierInvoiceManualInput {
  supplierId: string;
  companyId: string;
  docTipoCode: string;
  documentType?: string | null;
  folio: string;
  emissionDate: string;
  dueDate?: string | null;
  dueDateEstimated?: boolean;
  exento: number;
  neto: number;
  iva: number;
  imptoEspecifico: number;
  totalClp: number;
  saldoClp: number;
  glosa?: string | null;
  manualPdfUrl?: string | null;
}

export interface SupplierInvoiceFilters {
  search?: string;
  doc_tipo_code?: string;
  dateFrom?: string;
  dateTo?: string;
  /** "with" = has any PDF (BSale or manual); "without" = missing both. */
  pdfStatus?: "with" | "without";
  company_id?: string;
}

/** One line of the Libro de Compras (SII / F29) for a company + period. */
export interface LibroComprasRow {
  id: string;
  emission_date: string;
  doc_tipo_code: string;
  folio: string;
  rut: string;
  razon_social: string | null;
  exento: number;
  neto: number;
  iva: number;
  impto_especifico: number;
  total_clp: number;
  /** Correlativo interno = número de comprobante del asiento (account_movements.move_number). */
  corr_interno: string | null;
}

// =============================================================================
// AP Payments (pago a proveedor) + corte histórico
// =============================================================================

/** A paid document is either a going-forward factura or an opening-balance (saldo inicial) row. */
export type PaymentDocSource = "invoice" | "opening";

/** One document to settle in a payment nómina (amount is always positive). */
export interface PayItem {
  source: PaymentDocSource;
  doc_id: string;
  amount: number;
}

export interface PaySupplierInvoicesInput {
  paymentDate: string; // YYYY-MM-DD
  bankAccountId: string;
  items: PayItem[];
  ref?: string | null;
  /** Admins bypass the closed-period guard (mirrors movementService). */
  isAdmin?: boolean;
}

export interface PaySupplierInvoicesResult {
  payment_id: string;
  movement_id: string;
  total: number;
}

// An open payable document (factura or saldo inicial) across suppliers, from
// get_supplier_payable_documents — feeds the global "Pagos a Proveedor" view. `payable_clp` is the
// positive magnitude owed (facturas store it positive, saldo inicial negative; both normalized here).
export interface PayableDocument {
  doc_id: string;
  source: PaymentDocSource;
  supplier_id: string;
  supplier_name: string;
  supplier_rut: string | null;
  company_id: string;
  company_name: string | null;
  ap_account_code: string | null;
  doc_tipo_code: string | null;
  folio: string | null;
  emission_date: string | null;
  due_date: string | null;
  payable_clp: number;
  estado: OpeningBalanceEstado;
  // Factura contabilizada (asiento publicado). Saldo inicial => siempre true. Las no contabilizadas
  // se muestran pero no se pueden pagar (el RPC pay_supplier_invoices también las rechaza).
  is_posted: boolean;
}

// One open document at a cutoff date (line-level), from get_supplier_documents_at_cutoff —
// feeds the two aging reports (Consolidado / Por código).
export interface SupplierDocumentAtCutoff {
  supplier_id: string;
  softland_code: string | null;
  nombre: string;
  cuenta: string;
  doc_tipo_code: string | null;
  folio: string | null;
  emission_date: string | null;
  due_date: string | null;
  exchange_rate: number | null;
  saldo_at: number;
  estado: OpeningBalanceEstado;
  // Glosa del asiento: account_movements.ref para facturas contabilizadas; null para saldos
  // iniciales (no se contabilizan como asiento).
  glosa: string | null;
}

export interface OilType {
  id: string;
  name: string;
  code: string;
  description: string | null;
  density_kg_per_liter: number;
  current_stock_liters: number;
  minimum_stock_liters: number | null;
  average_cost_clp: number | null;
  created_at: string;
  updated_at: string;
}

export interface RawMaterial {
  id: string;
  name: string;
  code: string;
  description: string | null;
  unit: string;
  current_stock: number;
  minimum_stock_level: number | null;
  average_cost_clp: number | null;
  exclude_from_inventory: boolean;
  group_code: string | null;
  group_description: string | null;
  is_returnable_totem: boolean;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrder {
  id: string;
  po_number: number;
  supplier_id: string;
  company_id: string;
  order_type: OrderType;
  issue_date: string;
  delivery_date: string | null;
  currency: Currency;
  exchange_rate_id: string | null;
  payment_terms: string | null;
  observations: string | null;
  subtotal: number;
  // Order-level discount, applied to the neto before IVA. `discount_value` is the raw input
  // (the % when percentage, the amount when amount); `discount_amount` is the resolved amount
  // frozen on the header. taxable base = subtotal - discount_amount.
  discount_type: DiscountType;
  discount_value: number;
  discount_amount: number;
  // VAT-exempt (exento de IVA): when true the OC adds no 19% IVA (tax_amount frozen at 0).
  // Mirrors sales_request.is_exento (factura exenta). Set from a VAT-exempt supplier cotización.
  is_exento: boolean;
  tax_amount: number;
  ila_amount: number;
  total: number;
  status: PurchaseOrderStatus;
  received_at: string | null;
  received_by: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined relations
  supplier?: Supplier;
  items?: PurchaseOrderItem[];
  receptions?: PurchaseOrderReception[];
}

export interface PurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  ingredient_type: ItemType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  product_id: string | null;
  description: string;
  quantity: number;
  received_quantity: number;
  unit: string;
  unit_price: number;
  // Per-line discount (mirrors the cotización "Rec/Desc" column). `discount_value` is the raw
  // input (% when percentage, CLP when amount); `discount_amount` is the resolved amount frozen
  // on the line. `subtotal` is the NET line total: quantity * unit_price - discount_amount.
  discount_type: DiscountType;
  discount_value: number;
  discount_amount: number;
  subtotal: number;
  ila_rate: number;
  created_at: string;
  // Joined relations
  oil_type?: OilType;
  raw_material?: RawMaterial;
  product?: {
    id: string;
    short_name: string;
    barcode?: string;
    net_price?: number;
  };
  // Computed - remaining to receive
  pending_quantity?: number;
}

// Reception tracking types
export interface PurchaseOrderReception {
  id: string;
  purchase_order_id: string;
  reception_date: string;
  plant: Plant;
  received_by: string | null;
  observations: string | null;
  created_at: string;
  // GR/IR asiento (account_movements) posted at reception for test-company SKUs;
  // null for real-company receptions (which post no accounting entry).
  account_movement_id: string | null;
  // Joined relations
  items?: PurchaseOrderReceptionItem[];
  received_by_profile?: { id: string; name: string };
}

export interface PurchaseOrderReceptionItem {
  id: string;
  reception_id: string;
  purchase_order_item_id: string;
  quantity_received: number;
  actual_unit_cost: number;
  expiry_date: string | null;
  created_at: string;
  // Joined relations
  purchase_order_item?: PurchaseOrderItem;
}

// A reception joined with its parent purchase order — used by the Ingreso ledger,
// which lists every reception across all POs (not scoped to a single order).
export interface ReceptionWithOrder extends PurchaseOrderReception {
  purchase_order?: {
    id: string;
    po_number: number;
    order_type: OrderType;
    currency: Currency;
    supplier?: { name: string } | null;
  };
}

// Form data for creating a reception
export interface ReceptionItemFormData {
  purchase_order_item_id: string;
  quantity_received: number;
  actual_unit_cost: number;
  expiry_date?: string;
}

export interface ReceptionFormData {
  purchase_order_id: string;
  reception_date: string;
  plant: Plant;
  observations?: string;
  items: ReceptionItemFormData[];
}

// =============================================================================
// Stock Movement Types
// =============================================================================

export type StockMovementType =
  | "reception"
  | "production_consume"
  | "adjustment"
  | "maquila_consume";
export type StockReferenceType =
  | "purchase_order_reception"
  | "production_batch"
  | "manual"
  | "maquila_consumption";

export interface IngredientStockMovement {
  id: string;
  ingredient_type: IngredientType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  movement_type: StockMovementType;
  quantity: number;
  unit: string;
  balance_after: number;
  reference_type: StockReferenceType | null;
  reference_id: string | null;
  reference_number: string | null;
  unit_cost: number | null;
  total_cost: number | null;
  currency: string;
  notes: string | null;
  performed_by: string | null;
  plant: Plant;
  created_at: string;
  // Joined relations
  oil_type?: OilType;
  raw_material?: RawMaterial;
  performed_by_profile?: { id: string; name: string };
}

export interface StockAdjustmentFormData {
  ingredient_type: IngredientType;
  oil_type_id?: string;
  raw_material_id?: string;
  quantity: number;
  unit: string;
  notes: string;
}

export interface OilTypePrice {
  id: string;
  oil_type_id: string;
  supplier_id: string | null;
  unit_price: number;
  currency: Currency;
  effective_date: string;
  created_at: string;
  // Joined relations
  oil_type?: OilType;
  supplier?: Supplier;
}

export interface RawMaterialPrice {
  id: string;
  raw_material_id: string;
  supplier_id: string | null;
  unit_price: number;
  currency: Currency;
  effective_date: string;
  created_at: string;
  // Joined relations
  raw_material?: RawMaterial;
  supplier?: Supplier;
}

// =============================================================================
// Form Data Types
// =============================================================================

export interface SupplierFormData {
  name: string;
  rut?: string;
  giro?: string;
  address?: string;
  comuna?: string;
  phone?: string;
  email?: string;
  contact_name?: string;
  notes?: string;
  is_active?: boolean;
  razon_social?: string;
  payment_condition?: string;
  bank_name?: string;
  bank_account?: string;
}

export interface OilTypeFormData {
  name: string;
  code: string;
  description?: string;
  density_kg_per_liter?: number;
  minimum_stock_liters?: number;
}

export interface RawMaterialFormData {
  name: string;
  code: string;
  description?: string;
  unit: string;
  minimum_stock_level?: number;
  exclude_from_inventory?: boolean;
  group_description?: string;
  is_returnable_totem?: boolean;
}

export interface PurchaseOrderItemFormData {
  id?: string; // For updating existing items
  ingredient_type: ItemType;
  oil_type_id?: string;
  raw_material_id?: string;
  product_id?: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  // Per-line discount applied to the line gross (quantity * unit_price). `discount_value` holds
  // the % or the fixed amount depending on `discount_type`; the resolved amount is derived in the
  // service/form via resolveLineAmounts.
  discount_type?: DiscountType;
  discount_value?: number;
  ila_rate?: number; // ILA percentage (0, 10, 18, 20.5, 31.5); Misceláneos only
}

export interface PurchaseOrderFormData {
  supplier_id: string;
  company_id?: string;
  order_type: OrderType;
  issue_date: string;
  delivery_date?: string;
  currency: Currency;
  exchange_rate_id?: string;
  payment_terms?: string;
  observations?: string;
  // Order-level discount applied to the neto before IVA. `discount_value` holds the % or the
  // fixed amount depending on `discount_type`; the resolved amount is derived in the service.
  discount_type?: DiscountType;
  discount_value?: number;
  // VAT-exempt cotización: when true no 19% IVA is added (tax_amount = 0). Default false.
  is_exento?: boolean;
  items: PurchaseOrderItemFormData[];
}

// =============================================================================
// Filter Types
// =============================================================================

export interface PurchaseOrderFilters {
  status?: PurchaseOrderStatus;
  supplier_id?: string;
  order_type?: OrderType;
  dateFrom?: string;
  dateTo?: string;
}

export interface SupplierFilters {
  is_active?: boolean;
  search?: string;
}

// =============================================================================
// Display Helpers
// =============================================================================

export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> =
  {
    draft: "Borrador",
    // DB value stays "sent"; the OC lifecycle surfaces it to users as "Aprobado".
    sent: "Aprobado",
    partial: "Parcial",
    received: "Recibido",
    cancelled: "Cancelado",
  };

export const PURCHASE_ORDER_STATUS_COLORS: Record<PurchaseOrderStatus, string> =
  {
    draft: "bg-gray-100 text-gray-800",
    sent: "bg-green-100 text-green-800",
    partial: "bg-blue-100 text-blue-800",
    received: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
  };

// The printed OC only distinguishes Borrador from Aprobado: once a draft is
// approved, every downstream lifecycle state (sent/partial/received) prints the
// single "Aprobado" stamp; a cancelled order still prints "Cancelado".
export const purchaseOrderPdfStatusLabel = (
  status: PurchaseOrderStatus
): string =>
  status === "draft" || status === "cancelled"
    ? PURCHASE_ORDER_STATUS_LABELS[status]
    : "Aprobado";

export const INGREDIENT_TYPE_LABELS: Record<IngredientType, string> = {
  oil: "Aceite",
  raw_material: "Material",
};

export const CURRENCY_LABELS: Record<Currency, string> = {
  CLP: "Peso Chileno (CLP)",
  USD: "Dólar (USD)",
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CLP: "$",
  USD: "US$",
};

// Common units for oil purchases
export const OIL_UNITS = [
  { value: "tonnes", label: "Toneladas" },
  { value: "kg", label: "Kilogramos" },
  { value: "liters", label: "Litros" },
  { value: "units", label: "Unidades" },
];

// Common units for raw materials. Canonical codes from src/constants/units.ts.
// Keep aligned with MEASUREMENT_UNITS so new rows can't introduce drift.
export const MATERIAL_UNITS = [
  { value: "UNI", label: "Unidades" },
  { value: "KGS", label: "Kilogramos" },
  { value: "LTS", label: "Litros" },
  { value: "CAJA", label: "Cajas" },
  { value: "MNG", label: "Mangas" },
  { value: "SSGG", label: "SSGG" },
];

// Unit labels mapping for display. Canonical codes first; legacy lowercase
// keys retained so historical rows in other tables still render correctly.
export const UNIT_LABELS: Record<string, string> = {
  KGS: "Kilogramos",
  LTS: "Litros",
  UNI: "Unidades",
  CAJA: "Cajas",
  MNG: "Mangas",
  SSGG: "SSGG",
  tonnes: "Toneladas",
  kg: "Kilogramos",
  liters: "Litros",
  units: "Unidades",
  boxes: "Cajas",
};

// Order type labels
export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  ingredients: "Ingredientes",
  supplies: "Misceláneos",
  products: "Productos",
};

// ILA (Impuesto Adicional a las Bebidas Alcohólicas y Analcohólicas) rates, per SII:
// 10% / 18% bebidas analcohólicas (bajo / sobre umbral de azúcar),
// 20.5% vinos-cervezas-sidras, 31.5% licores-piscos-destilados. 0 = exenta.
export const ILA_RATES = [0, 10, 18, 20.5, 31.5] as const;

export const formatIlaRate = (rate: number): string =>
  `${new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 }).format(rate)}%`;

export const DISCOUNT_TYPE_LABELS: Record<DiscountType, string> = {
  none: "Sin descuento",
  percentage: "Porcentaje (%)",
  amount: "Monto fijo",
};

// Resolve the order-level discount into a currency amount off the neto, clamped to [0, neto].
// Percentage discounts round to the nearest unit (consistent with how tax/ILA are frozen on
// the header). Returns 0 when there is no discount. Single source of truth for the form, the
// service's calculateTotals, and the detail Resumen so they never drift.
export const resolveDiscountAmount = (
  subtotal: number,
  discountType: DiscountType | null | undefined,
  discountValue: number | null | undefined
): number => {
  const value = discountValue ?? 0;
  if (
    !discountType ||
    discountType === "none" ||
    value <= 0 ||
    subtotal <= 0
  ) {
    return 0;
  }
  const raw =
    discountType === "percentage"
      ? Math.round((subtotal * value) / 100)
      : value;
  return Math.min(Math.max(raw, 0), subtotal);
};

// Resolve a single purchase-order line into its gross, discount and net amounts. The per-line
// discount mirrors the supplier cotización's "Rec/Desc" column: a percentage of the line gross
// or a fixed CLP amount, clamped to [0, gross]. Single source of truth for the form, the
// service's calculateTotals, the detail Resumen and the OC PDF so they never drift.
export const resolveLineAmounts = (
  quantity: number | null | undefined,
  unitPrice: number | null | undefined,
  discountType?: DiscountType | null,
  discountValue?: number | null
): { gross: number; discount: number; net: number } => {
  const gross = (quantity ?? 0) * (unitPrice ?? 0);
  const discount = resolveDiscountAmount(gross, discountType, discountValue);
  return { gross, discount, net: gross - discount };
};

// Item type labels (extended from ingredient type)
export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  oil: "Aceite",
  raw_material: "Material",
  product: "Producto",
  misc: "Misceláneo",
};

// Starter suggestions for Misceláneos (supplies) line descriptions. These are
// UI hints ONLY — not a services master/catalog. The combobox merges them with
// the distinct descriptions already used in past misc POs; selecting or typing
// a value just writes the free-text `description`, so nothing is persisted to a
// catalog (per Jose: "no llenar el maestro de servicios").
export const MISC_DESCRIPTION_SUGGESTIONS = [
  "Agua potable",
  "Internet",
  "Electricidad",
  "Gas",
  "Arriendo",
  "Consultoría",
  "Flete",
  "Mantención",
  "Aseo",
  "Útiles de oficina",
];

// Common units for product purchases
export const PRODUCT_UNITS = [
  { value: "units", label: "Unidades" },
  { value: "boxes", label: "Cajas" },
];

// Stock movement type labels
export const MOVEMENT_TYPE_LABELS: Record<StockMovementType, string> = {
  reception: "Recepción",
  production_consume: "Producción",
  adjustment: "Ajuste",
  maquila_consume: "Consumo Maquila",
};

// Stock movement type colors
export const MOVEMENT_TYPE_COLORS: Record<StockMovementType, string> = {
  reception: "bg-green-100 text-green-800",
  production_consume: "bg-orange-100 text-orange-800",
  adjustment: "bg-blue-100 text-blue-800",
  maquila_consume: "bg-purple-100 text-purple-800",
};

// Plant options for reception (physical plants only — a PO is never received into
// the No Conforme quarantine bucket).
export const PLANT_OPTIONS: { value: Plant; label: string }[] = [
  { value: "P1", label: "Planta 1 (P1)" },
  { value: "P2", label: "Planta 2 (P2)" },
];

// Plant options for traslados: physical plants plus the No Conforme bucket, so
// non-conforming ingredient stock can be moved into (and released back out of) NC.
export const TRANSFER_PLANT_OPTIONS: { value: Plant; label: string }[] = [
  ...PLANT_OPTIONS,
  { value: "NC", label: "No Conforme (NC)" },
];

export const PLANT_LABELS: Record<Plant, string> = {
  P1: "Planta 1",
  P2: "Planta 2",
  NC: "No Conforme",
};

// Producto-terminado WMS bodegas map 1:1 to plants (B1≡Planta 1, B2≡Planta 2,
// NC≡NC). PT transfers are shown in the same Planta vocabulary as ingredient
// transfers, so a bodega code is rendered as its plant label.
const WAREHOUSE_TO_PLANT: Record<string, Plant> = { B1: "P1", B2: "P2", NC: "NC" };
export const warehouseToPlantLabel = (
  code: string | null | undefined
): string => {
  if (!code) return "—";
  const plant = WAREHOUSE_TO_PLANT[code];
  return plant ? PLANT_LABELS[plant] : code;
};
